'use client';

import { memo, useCallback, useEffect, useRef } from 'react';

import { usePluginAnalytics, useTrackPluginInitialization } from '@air/plugin-ui';
import { type PremiereToAirHostInfo } from '@air/premiere-constants';

import { usePremiereMessaging } from '~/hooks/usePremiereMessaging';
import { sendReadyMessage } from '~/lib/pluginMessages';
import { usePremiereHostStore } from '~/stores/usePremiereHostStore';

export const PluginInitializationListener = memo(() => {
  const { trackPluginInitialization } = useTrackPluginInitialization();
  const { setGlobalEventProps } = usePluginAnalytics();
  const setHost = usePremiereHostStore((state) => state.setHost);
  const hasTrackedInitialization = useRef(false);

  const trackInitializationOnce = useCallback(() => {
    if (hasTrackedInitialization.current) {
      return;
    }
    hasTrackedInitialization.current = true;
    setGlobalEventProps({ app_name: 'PremierePlugin' });
    trackPluginInitialization();
  }, [setGlobalEventProps, trackPluginInitialization]);

  const handleHostReady = useCallback(() => {
    trackInitializationOnce();
  }, [trackInitializationOnce]);

  // Captured into `usePremiereHostStore` so the OAuth-callback page can pick
  // the variant-specific URL scheme (`adobe+ppro://` vs `adobe+pprobeta://`)
  // when deep-linking back into the app that launched the auth flow.
  const handleHostInfo = useCallback(
    (data: PremiereToAirHostInfo['data']) => {
      setHost(data);
    },
    [setHost],
  );

  usePremiereMessaging({
    onHostInfo: handleHostInfo,
    onReady: handleHostReady,
  });

  // One-shot per panel mount — empty deps so sendReadyMessage never re-fires
  // (re-firing would let the host pin a stale WebView instance).
  useEffect(() => {
    sendReadyMessage();
  }, []);

  // Fire when React is ready (Figma/Sanity pattern). handleHostReady is a
  // fallback when the host echoes Ready after webview `load` raced the listener.
  useEffect(() => {
    trackInitializationOnce();
  }, [trackInitializationOnce]);

  return null;
});

PluginInitializationListener.displayName = 'PluginInitializationListener';
